import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { faFileSignature, faList } from "@fortawesome/pro-regular-svg-icons";
import { faCalendarCirclePlus, faUserGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid2 as Grid, Stack } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePostService, usePutService } from "api/services/appointment/hooks";
import type { HcpServiceManager, ServicePayload } from "api/services/appointment/schemas/hcp_service";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext";
import { MUIDropdown, MUITextInput } from "shared/atoms/inputs";
import { MUISwitch } from "shared/atoms/MUI";
import { WidgetBox } from "shared/containers/WidgetBox";

import type { FormStates } from "../helpers/misc";
import { useServiceFormOptions } from "../hooks/useServiceFormOptions";

import { MultiselectField } from "./MultiselectField";

export type ServiceFormData = {
  name: string | undefined;
  description: string | undefined | null;
  health_care_provider_service_category_id: number | undefined;
  purpose: string | undefined | null;
  mode: string;
  health_questionnaire_type: string | undefined;
  capacity: number | undefined;
  opening_hours: number | undefined;
  closing_hours: number | undefined;
  duration: number | undefined;
  no_earlier_than: number | undefined;
  no_later_than: number | undefined;
  payment_methods: string[] | undefined | null;
  health_care_professional_ids: string[] | undefined;
  bookable_by_patients: boolean;
};

interface Props {
  onLoading?: (value: boolean) => void;
  onSuccess?: () => void;
  service: HcpServiceManager | undefined;
}

export interface ServiceFormRef {
  submitForm: () => Promise<void>;
}

export const ServiceForm = forwardRef<ServiceFormRef, Props>(({ service, onLoading, onSuccess }, ref) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { showSnackbar } = useSnackbarContext();
  invariant(profile);
  const [formDisabled, setFormDisabled] = useState(false);
  const [startTime, setStartTime] = useState<number | undefined>(undefined);
  const [formState, setFormState] = useState<FormStates>("default");

  const form = useForm<ServiceFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      bookable_by_patients: false,
    },
  });
  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    watch,
    reset,
    control,
  } = form;

  // APIs
  const postServiceMutation = usePostService();
  const putServiceMutation = usePutService();

  // Options
  const {
    serviceTemplate,
    optionsWorkHours,
    optionsNoEarlierThan,
    optionsNoLaterThan,
    optionsHealthQuestionnaireType,
    optionsHealthCareProfessionals,
    optionsCategory,
    optionsPurpose,
    optionsDuration,
    optionsMode,
    optionsPaymentMethods,
  } = useServiceFormOptions(profile.id);

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      await onSubmit();
    },
  }));

  useEffect(() => {
    onLoading?.(formState === "saving");
  }, [formState]);

  const onSubmit = handleSubmit(formData => {
    const data: ServicePayload = {
      name: formData.name ?? "",
      description: formData.description ?? "",
      health_care_provider_service_category_id: formData.health_care_provider_service_category_id
        ? parseInt(formData.health_care_provider_service_category_id.toString(), 10)
        : null,
      capacity: formData.capacity ? parseInt(formData.capacity.toString(), 10) : null,
      mode: formData.mode,
      payment_methods: formData.payment_methods ?? [],
      purpose: formData.purpose ?? "",
      opening_hours: formData.opening_hours ?? null,
      closing_hours: formData.closing_hours ?? null,
      duration: formData.duration ? parseInt(formData.duration.toString(), 10) : null,
      no_earlier_than: formData.no_earlier_than ?? null,
      no_later_than: formData.no_later_than ?? null,
      health_questionnaire_type: formData.health_questionnaire_type ?? "",
      health_care_professional_ids: formData.health_care_professional_ids
        ? formData.health_care_professional_ids.map(option => parseInt(option.toString(), 10))
        : [],
      bookable_by_patients: formData.bookable_by_patients ?? null,
    };

    if (service) {
      putServiceMutation.mutateAsync(
        {
          data: { ...data, id: service.id },
          managerId: profile.id,
        },
        onSetupCallback()
      );
    } else {
      postServiceMutation.mutateAsync(
        {
          data,
          managerId: profile.id,
        },
        onSetupCallback()
      );
    }
  });

  const onSetupCallback = () => {
    setFormState("saving");
    setFormDisabled(true);
    return {
      onSuccess: () => {
        showSnackbar({
          message: service ? t("booking.services.successfully_updated") : t("booking.services.successfully_created"),
          severity: "success",
        });
        setFormState("success");
        onSuccess?.();
      },
      onError() {
        showSnackbar({
          message: t("errors.generic"),
          severity: "error",
        });
        setFormState("default");
        setFormDisabled(false);
      },
    };
  };

  // Set the form default values
  // updated upon service or template change
  useEffect(() => {
    reset();

    if (service) {
      setFormValues({
        ...service,
        health_care_professional_ids: service.health_care_professionals
          ? service.health_care_professionals.map(option => option.id.toString())
          : [],
      });
    } else {
      setNewServiceFormValues();
    }
  }, [service, serviceTemplate]);

  // controls and updates the end time - it cannot be before the start time
  const workdayStartChange = () => {
    const currentStart = getValues("opening_hours");
    const currentEnd = getValues("closing_hours");
    setStartTime(currentStart);
    if (currentStart && currentEnd && currentStart > currentEnd) {
      setValue("closing_hours", currentStart);
    }
  };

  // default values when new service is selected
  const setNewServiceFormValues = () => {
    setValue(
      "health_care_professional_ids",
      optionsHealthCareProfessionals.map(option => option.value.toString())
    );
  };

  const setFormValues = (currentService: ServiceFormData) => {
    setValue("name", currentService.name);
    setValue("capacity", currentService.capacity);
    setValue("purpose", currentService.purpose);
    setValue("health_care_provider_service_category_id", currentService.health_care_provider_service_category_id);
    setValue("mode", currentService.mode);
    setValue("health_questionnaire_type", currentService.health_questionnaire_type);

    setValue("description", currentService.description);
    setValue("opening_hours", currentService.opening_hours);
    setValue("closing_hours", currentService.closing_hours);
    setValue("duration", currentService.duration);
    setValue("no_earlier_than", currentService.no_earlier_than);
    setValue("no_later_than", currentService.no_later_than);

    setValue("payment_methods", currentService.payment_methods);
    setValue("bookable_by_patients", currentService.bookable_by_patients);

    if (currentService.health_care_professional_ids)
      setValue(
        "health_care_professional_ids",
        currentService.health_care_professional_ids.map(option => option.toString())
      );
  };

  // Payment methods options are dependent on mode value
  const watchedPaymentMethods = watch("payment_methods");

  // Payment methods values are updated based on latest input - either free option or non free options
  useEffect(() => {
    if (watchedPaymentMethods?.includes("free")) {
      setValue("payment_methods", [watchedPaymentMethods.at(-1) ?? ""]);
    }
  }, [watchedPaymentMethods?.at(-1)]);

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} data-testid="service-form" noValidate>
        <Stack spacing={2} flexDirection="row" flexWrap="wrap">
          <Stack spacing={2} flex={1}>
            <WidgetBox
              title={t("booking.services.general_information")}
              icon={<FontAwesomeIcon icon={faFileSignature} />}
            >
              <MUITextInput
                name="name"
                label={t("booking.services.name")}
                disabled={formDisabled}
                required
                error={errors.name && t("errors.field.required")}
              />

              <MUITextInput
                name="description"
                label={t("booking.services.description")}
                disabled={formDisabled}
                required
                multiline
                error={errors.description && t("errors.field.required")}
              />

              <MUIDropdown
                label={t("booking.services.category")}
                name="health_care_provider_service_category_id"
                options={optionsCategory}
                disabled={formDisabled}
                required
                error={errors.health_care_provider_service_category_id && t("errors.field.required")}
              />
            </WidgetBox>
            <WidgetBox title={t("booking.services.details")} icon={<FontAwesomeIcon icon={faList} />}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUITextInput
                    label={t("booking.services.capacity")}
                    name="capacity"
                    disabled={formDisabled}
                    required
                    type="number"
                    min={1}
                    max={100}
                    error={errors.capacity && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.mode")}
                    name="mode"
                    options={optionsMode}
                    disabled={formDisabled}
                    required
                    error={errors.mode && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.opening_hours")}
                    name="opening_hours"
                    options={optionsWorkHours}
                    onChange={() => workdayStartChange()}
                    disabled={formDisabled}
                    required
                    error={errors.opening_hours && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.closing_hours")}
                    name="closing_hours"
                    options={optionsWorkHours.filter(option => (startTime ? option.value >= startTime : option.value))}
                    disabled={formDisabled}
                    required
                    error={errors.closing_hours && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.no_earlier_than")}
                    name="no_earlier_than"
                    options={optionsNoEarlierThan}
                    disabled={formDisabled}
                    required
                    error={errors.no_earlier_than && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.no_later_than")}
                    name="no_later_than"
                    options={optionsNoLaterThan}
                    disabled={formDisabled}
                    required
                    error={errors.no_later_than && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.duration")}
                    name="duration"
                    options={optionsDuration}
                    disabled={formDisabled}
                    required
                    error={errors.duration && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MUIDropdown
                    label={t("booking.services.health_questionnaire_type")}
                    name="health_questionnaire_type"
                    options={optionsHealthQuestionnaireType}
                    disabled={formDisabled}
                    required
                    error={errors.health_questionnaire_type && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <MUIDropdown
                    label={t("booking.services.purpose")}
                    name="purpose"
                    options={optionsPurpose}
                    disabled={formDisabled}
                    required
                    error={errors.purpose && t("errors.field.required")}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <MultiselectField
                    name="payment_methods"
                    label={t("booking.services.payment_methods")}
                    optionsFull={optionsPaymentMethods}
                    disabled={formDisabled}
                    required
                    error={errors.payment_methods && t("errors.field.required")}
                  />
                </Grid>
              </Grid>
            </WidgetBox>
          </Stack>

          <Stack spacing={2} flex={1}>
            <WidgetBox
              title={t("booking.services.clinicians")}
              description={t("booking.services.select_team_members")}
              icon={<FontAwesomeIcon icon={faUserGroup} />}
            >
              <MultiselectField
                name="health_care_professional_ids"
                label={t("booking.services.health_care_professionals")}
                optionsFull={optionsHealthCareProfessionals}
                disabled={formDisabled}
                required
                error={errors.health_care_professional_ids && t("errors.field.required")}
              />
            </WidgetBox>

            <WidgetBox
              title={t("booking.services.bookable")}
              description={t("booking.services.allow_patients_to_book")}
              icon={<FontAwesomeIcon icon={faCalendarCirclePlus} />}
            >
              <Controller
                control={control}
                name="bookable_by_patients"
                render={({ field }) => (
                  <MUISwitch
                    name="bookable_by_patients"
                    disabled={formDisabled}
                    onChange={event => {
                      field.onChange(event);
                    }}
                    checked={field.value}
                  />
                )}
              />
            </WidgetBox>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
});
